import React, { useState } from 'react';
import CalendarModal from './CalendarModal';
import {
  LineChart,
  Line,
  AreaChart,
  Area,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
} from 'recharts';
import styled, { createGlobalStyle, useTheme } from 'styled-components';
import { BarChart as BarChartIcon, TrendingUp, Clock, PieChart as PieChartIcon, Activity } from 'lucide-react';
import { motion } from 'framer-motion';
// Estilo global para garantir fundo escuro no tooltip e hover do PieChart
const TooltipGlobalStyle = createGlobalStyle`
  .recharts-tooltip-wrapper {
    background: ${props => props.theme.colors.backgroundTertiary} !important;
    color: ${props => props.theme.colors.textPrimary} !important;
    border-radius: ${props => props.theme.borders.radius.md} !important;
    border: 1px solid ${props => props.theme.colors.border} !important;
    box-shadow: ${props => props.theme.shadows.md} !important;
  }
  .recharts-surface {
    background: ${props => props.theme.colors.backgroundSecondary} !important;
  }
  .recharts-pie-sector,
  .recharts-active-shape {
    fill: ${props => props.theme.colors.backgroundSecondary} !important;
    stroke: ${props => props.theme.colors.primary} !important;
  }
`;

const ChartContainer = styled.div`
  background: ${props => props.theme.colors.backgroundSecondary};
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borders.radius.lg};
  padding: ${props => props.theme.spacing[6]};
  height: 100%;
  display: flex;
  flex-direction: column;
`;

const ChartHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: ${props => props.theme.spacing[6]};
`;

const ChartTitle = styled.h3`
  font-family: ${props => props.theme.typography.fontFamily.heading};
  font-size: ${props => props.theme.typography.fontSize.xl};
  font-weight: ${props => props.theme.typography.fontWeight.semibold};
  color: ${props => props.theme.colors.textPrimary};
  margin: 0;
`;

const ChartControls = styled.div`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing[2]};
`;

const ChartButton = styled.button<{ $active?: boolean }>`
  padding: ${props => props.theme.spacing[2]} ${props => props.theme.spacing[4]};
  background: ${props => props.$active ? props.theme.colors.primary : props.theme.colors.backgroundTertiary};
  border: 1px solid ${props => props.$active ? props.theme.colors.primary : props.theme.colors.border};
  border-radius: ${props => props.theme.borders.radius.md};
  color: ${props => props.$active ? props.theme.colors.textPrimary : props.theme.colors.textSecondary};
  font-size: ${props => props.theme.typography.fontSize.sm};
  font-weight: ${props => props.theme.typography.fontWeight.medium};
  cursor: pointer;
  transition: all ${props => props.theme.animations.duration.fast} ${props => props.theme.animations.easing.easeInOut};
  
  &:hover {
  background: ${props => props.$active ? props.theme.colors.primaryHover : props.theme.colors.backgroundSecondary};
    color: ${props => props.theme.colors.textPrimary};
  }
`;

const ChartContent = styled.div`
  flex: 1;
  width: 100%;
  min-height: 400px;
`;

const LoadingState = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 300px;
  color: ${props => props.theme.colors.textSecondary};
`;

const EmptyState = styled(motion.div)`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 300px;
  text-align: center;
  padding: ${props => props.theme.spacing[8]};
`;

const EmptyIcon = styled.div`
  width: 100px;
  height: 100px;
  border-radius: ${props => props.theme.borders.radius.full};
  background: linear-gradient(135deg, ${props => props.theme.colors.primary}15 0%, ${props => props.theme.colors.primary}08 100%);
  border: 2px solid ${props => props.theme.colors.primary}20;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: ${props => props.theme.spacing[4]};
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    width: 200%;
    height: 200%;
    background: linear-gradient(
      45deg,
      transparent 30%,
      ${props => props.theme.colors.primary}10 50%,
      transparent 70%
    );
    animation: shimmer 3s infinite;
  }

  @keyframes shimmer {
    0% { transform: translateX(-100%) translateY(-100%) rotate(45deg); }
    100% { transform: translateX(100%) translateY(100%) rotate(45deg); }
  }

  svg {
    color: ${props => props.theme.colors.primary};
    position: relative;
    z-index: 1;
  }
`;

const EmptyTitle = styled.h3`
  font-size: ${props => props.theme.typography.fontSize.xl};
  font-weight: ${props => props.theme.typography.fontWeight.bold};
  color: ${props => props.theme.colors.textPrimary};
  margin: 0 0 ${props => props.theme.spacing[2]} 0;
`;

const EmptyDescription = styled.p`
  font-size: ${props => props.theme.typography.fontSize.sm};
  color: ${props => props.theme.colors.textSecondary};
  margin: 0;
  line-height: 1.6;
`;

const EmptyBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: ${props => props.theme.spacing[2]};
  padding: ${props => props.theme.spacing[2]} ${props => props.theme.spacing[4]};
  margin-top: ${props => props.theme.spacing[4]};
  background: ${props => props.theme.colors.backgroundTertiary};
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borders.radius.full};
  font-size: ${props => props.theme.typography.fontSize.xs};
  font-weight: ${props => props.theme.typography.fontWeight.medium};
  color: ${props => props.theme.colors.textSecondary};

  svg {
    width: 14px;
    height: 14px;
    color: ${props => props.theme.colors.primary};
  }
`;

interface SalesChartProps {
  title: string;
  // data a qualquer formato compreensível pelos gráficos (string-keyed records)
  data: Array<Record<string, unknown>>;
  type: 'line' | 'area' | 'bar' | 'pie';
  dataKey: string;
  xAxisKey?: string;
  loading?: boolean;
  height?: number;
  showControls?: boolean;
  controls?: Array<{ label: string; value: string }>;
  onControlChange?: (value: string) => void;
  activeControl?: string;
}

const SalesChart: React.FC<SalesChartProps> = ({
  title,
  data,
  type,
  dataKey,
  xAxisKey = 'name',
  loading = false,
  height = 350,
  showControls = false,
  controls = [],
  onControlChange,
  activeControl
}) => {
  // Cores customizadas para status
  const theme = useTheme();
  const statusColors: Record<string, string> = {
    'pago': theme.colors.primary, // vermelho
    'concluida': theme.colors.success,
    'pendente': '#b3b3b3', // cinza
    'processando': '#b3b3b3',
    'cancelada': '#fff', // branco
    'falhou': theme.colors.error,
  };
  const colors = [
    theme.colors.primary, // pago
    theme.colors.success, // concluida
    '#b3b3b3', // pendente/processando
    '#fff', // cancelada
    theme.colors.error, // falhou
    theme.colors.primary // outros
  ];

  // Integração calendário
  const [calendarOpen, setCalendarOpen] = useState(false);
  const [selectedDay, setSelectedDay] = useState<number | null>(null);
  const [selectedMonth, setSelectedMonth] = useState<number | null>(null);
  const [selectedYear, setSelectedYear] = useState<number | null>(null);
  const [period, setPeriod] = useState<string>('30d');

  const handleCalendarSelect = (date: Date) => {
    setCalendarOpen(false);
    setSelectedDay(date.getDate());
    setSelectedMonth(date.getMonth());
    setSelectedYear(date.getFullYear());
    setPeriod('custom');
  };

  const handlePeriodChange = (value: string) => {
    setPeriod(value);
    if (value !== 'custom') {
      setSelectedDay(null);
      setSelectedMonth(null);
      setSelectedYear(null);
    }
  };

  // Filtra os dados do gráfico pelo mês/ano selecionado
  // Filtra por mês/ano usando dataVenda ou name (caso seja string mês)
  const filteredData = React.useMemo(() => {
    if (!Array.isArray(data) || typeof data[0] !== 'object') return data;
    if (type !== 'line' && type !== 'area') return data;
    // Filtro por período
    if (period === 'custom' && selectedDay !== null && selectedMonth !== null && selectedYear !== null) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      return data.filter((item: any) => {
        if (item.dataVenda) {
          const d = new Date(item.dataVenda);
          return d.getDate() === selectedDay && d.getMonth() === selectedMonth && d.getFullYear() === selectedYear;
        }
        if (item.name && typeof item.name === 'string') {
          const [dia, mes, ano] = item.name.split('/').map(Number);
          if (!isNaN(dia) && !isNaN(mes) && !isNaN(ano)) {
            return dia === selectedDay && (mes - 1) === selectedMonth && ano === selectedYear;
          }
        }
        return true;
      });
    }
    // Filtro por 7d, 30d, 90d
    if (period === '7d' || period === '30d' || period === '90d') {
      const dias = parseInt(period);
      const hoje = new Date();
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      return data.filter((item: any) => {
        let d;
        if (item.dataVenda) d = new Date(item.dataVenda);
        else if (item.name && typeof item.name === 'string') {
          const [dia, mes, ano] = item.name.split('/').map(Number);
          if (!isNaN(dia) && !isNaN(mes) && !isNaN(ano)) d = new Date(ano, mes - 1, dia);
        }
        if (!d) return true;
        const diff = (hoje.getTime() - d.getTime()) / (1000 * 60 * 60 * 24);
        return diff >= 0 && diff < dias;
      });
    }
    return data;
  }, [data, type, period, selectedDay, selectedMonth, selectedYear]);

  const renderChart = () => {
    if (loading) {
      return (
        <LoadingState>
          Carregando gráfico...
        </LoadingState>
      );
    }

    if (!data || data.length === 0) {
      // Ícone baseado no tipo de gráfico
      const emptyIcon = () => {
        switch (type) {
          case 'line': return <Activity size={48} />;
          case 'area': return <TrendingUp size={48} />;
          case 'bar': return <BarChartIcon size={48} />;
          case 'pie': return <PieChartIcon size={48} />;
          default: return <TrendingUp size={48} />;
        }
      };

      return (
        <EmptyState
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: [0.34, 1.56, 0.64, 1] }}
        >
          <EmptyIcon>
            {emptyIcon()}
          </EmptyIcon>
          <EmptyTitle>Nenhum dado disponível</EmptyTitle>
          <EmptyDescription>
            Os dados de vendas aparecerão aqui quando houver registros.
          </EmptyDescription>
        </EmptyState>
      );
    }

    if (!filteredData || filteredData.length === 0) {
      return (
        <EmptyState
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: [0.34, 1.56, 0.64, 1] }}
        >
          <EmptyIcon>
            <BarChartIcon size={48} />
          </EmptyIcon>
          <EmptyTitle>Nenhuma venda encontrada</EmptyTitle>
          <EmptyDescription>
            Não há vendas registradas para o período selecionado.
          </EmptyDescription>
        </EmptyState>
      );
    }

    const commonProps = {
      data,
      margin: { top: 5, right: 30, left: 20, bottom: 5 }
    };

    switch (type) {
      case 'line':
        return (
          <ResponsiveContainer width="100%" height="100%">
            <LineChart {...commonProps} data={filteredData}>
              <CartesianGrid strokeDasharray="3 3" stroke={theme.colors.border} opacity={0.1} />
              <XAxis
                dataKey={xAxisKey}
                stroke={theme.colors.textSecondary}
                fontSize={12}
              />
              <YAxis
                stroke={theme.colors.textSecondary}
                fontSize={12}
              />
              <Tooltip
                contentStyle={{
                  backgroundColor: theme.colors.backgroundTertiary,
                  border: `1px solid ${theme.colors.border}`,
                  borderRadius: theme.borders.radius.md,
                  color: theme.colors.textPrimary
                }}
              />
              <Line
                type="monotone"
                dataKey={dataKey}
                stroke={theme.colors.primary}
                strokeWidth={3}
                dot={{ fill: theme.colors.primary, strokeWidth: 2, r: 4 }}
                activeDot={{ r: 6, stroke: theme.colors.primary, strokeWidth: 2 }}
              />
            </LineChart>
          </ResponsiveContainer>
        );

      case 'area':
        return (
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart {...commonProps} data={filteredData}>
              <CartesianGrid strokeDasharray="3 3" stroke={theme.colors.border} opacity={0.1} />
              <XAxis
                dataKey={xAxisKey}
                stroke={theme.colors.textSecondary}
                fontSize={12}
              />
              <YAxis
                stroke={theme.colors.textSecondary}
                fontSize={12}
              />
              <Tooltip
                contentStyle={{
                  backgroundColor: theme.colors.backgroundTertiary,
                  border: `1px solid ${theme.colors.border}`,
                  borderRadius: theme.borders.radius.md,
                  color: theme.colors.textPrimary
                }}
              />
              <Area
                type="monotone"
                dataKey={dataKey}
                stroke={theme.colors.primary}
                fill={`url(#colorGradient)`}
                strokeWidth={2}
              />
              <defs>
                <linearGradient id="colorGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor={theme.colors.primary} stopOpacity={0.3} />
                  <stop offset="95%" stopColor={theme.colors.primary} stopOpacity={0.1} />
                </linearGradient>
              </defs>
            </AreaChart>
          </ResponsiveContainer>
        );

      case 'bar':
        return (
          <ResponsiveContainer width="100%" height="100%">
            <BarChart {...commonProps}>
              <CartesianGrid strokeDasharray="3 3" stroke={theme.colors.border} opacity={0.1} />
              <XAxis
                dataKey={xAxisKey}
                stroke={theme.colors.textSecondary}
                fontSize={12}
              />
              <YAxis
                stroke={theme.colors.textSecondary}
                fontSize={12}
              />
              <Tooltip
                contentStyle={{
                  backgroundColor: theme.colors.backgroundTertiary,
                  border: `1px solid ${theme.colors.border}`,
                  borderRadius: theme.borders.radius.md,
                  color: theme.colors.textPrimary
                }}
              />
              <Bar
                dataKey={dataKey}
                fill={theme.colors.primary}
                radius={[4, 4, 0, 0]}
              />
            </BarChart>
          </ResponsiveContainer>
        );

      case 'pie':
        // PieChart sem labels centrais, apenas legenda responsiva abaixo
        return (
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={data}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={false}
                outerRadius={80}
                fill="#8884d8"
                dataKey={dataKey}
              >
                {data.map((entry, index) => {
                  // Cor por status
                  const name = (entry as Record<string, unknown>)['name'];
                  const status = typeof name === 'string' ? name.toLowerCase() : '';
                  const color = statusColors[status] || colors[index % colors.length];
                  return <Cell key={`cell-${index}`} fill={color} stroke={color === '#fff' ? theme.colors.border : color} />;
                })}
              </Pie>
              <Tooltip
                contentStyle={{
                  backgroundColor: theme.colors.backgroundTertiary,
                  border: `1px solid ${theme.colors.border}`,
                  borderRadius: theme.borders.radius.md,
                  color: theme.colors.textPrimary
                }}
              />
              <Legend
                layout="horizontal"
                align="center"
                verticalAlign="bottom"
                wrapperStyle={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  justifyContent: 'center',
                  fontSize: 14,
                  marginTop: 12,
                  gap: 16,
                  color: theme.colors.textSecondary
                }}
              />
            </PieChart>
          </ResponsiveContainer>
        );

      default:
        return null;
    }
  };

  return (
    <>
      <TooltipGlobalStyle />
      <ChartContainer>
        <ChartHeader>
          <ChartTitle>{title}</ChartTitle>
          {title?.toLowerCase().includes('período') && (
            <>
              <ChartButton onClick={() => setCalendarOpen(true)} style={{ marginLeft: 16 }}>
                Selecionar Data
              </ChartButton>
            </>
          )}
          {showControls && controls.length > 0 && (
            <ChartControls>
              {controls.map((control) => (
                <ChartButton
                  key={control.value}
                  $active={period === control.value}
                  onClick={() => handlePeriodChange(control.value)}
                >
                  {control.label}
                </ChartButton>
              ))}
            </ChartControls>
          )}
        </ChartHeader>
        <ChartContent>
          {renderChart()}
        </ChartContent>
        {/* Modal calendário melhorado */}
        <CalendarModal
          isOpen={calendarOpen}
          onClose={() => setCalendarOpen(false)}
          onSelect={handleCalendarSelect}
        />
      </ChartContainer>
    </>
  );
};

export default SalesChart;

